import React from 'react';
import { EXPLORIT_GLYPH_CODES } from './exploritGlyphCodes';
import { nav_icon_font, nav_icon_scale } from './navIconScale';
import { sanitize_theme_logo_data_uri } from '../../services/image/sanitizeThemeLogoSvg';

/**
 * Resolve mobile.navigation.tabs[].icon the same way as the public app:
 * - logo → app logo (preview uses FA home)
 * - icomoon, or the legacy alias explorit → IcoMoon glyph, else Font Awesome remap
 * - font-awesome → that glyph, no remap
 */
export function resolve_nav_icon(icon) {
    if (!icon || typeof icon !== 'object')
        return { kind: 'fa', className: 'fa fa-circle-o', scale: 1 };

    if (icon.type === 'logo')
        return { kind: 'logo', scale: 1 };

    const raw_name = String(icon.name || '').trim();
    if (!raw_name)
        return { kind: 'fa', className: 'fa fa-circle-o', scale: 1 };

    const font = nav_icon_font(icon.type) || (icon.type == null ? 'icomoon' : null);

    if (font === 'icomoon') {
        const code = EXPLORIT_GLYPH_CODES[raw_name]
            ?? EXPLORIT_GLYPH_CODES[raw_name.toLowerCase()];
        if (code != null)
            return { kind: 'icomoon', code, scale: nav_icon_scale('icomoon', raw_name) };
    }

    if (font === 'font-awesome')
        return { kind: 'fa', className: `fa fa-${raw_name}`, scale: nav_icon_scale('font-awesome', raw_name) };

    let fa_name = raw_name;
    if (fa_name === 'cart')
        fa_name = 'shopping-cart';
    else if (fa_name === 'agenda')
        fa_name = 'calendar';
    else if (fa_name === 'ticket')
        fa_name = 'ticket-alt';

    return { kind: 'fa', className: `fa fa-${fa_name}`, scale: nav_icon_scale('font-awesome', fa_name) };
}

/** Renders a nav tab icon for Kronos mobile previews. */
export function ExploritNavIcon({ icon, className = '', logoUri = '' }) {
    const resolved = resolve_nav_icon(icon);
    const extra = className ? ` ${className}` : '';
    const style = resolved.scale === 1 ? undefined : { transform: `scale(${resolved.scale})` };

    if (resolved.kind === 'logo') {
        const uri = sanitize_theme_logo_data_uri(logoUri) || logoUri;
        if (!uri)
            return <span className={ `nav-tab-logo nav-tab-logo--empty${extra}` } aria-hidden="true" />;
        // Same colour as the neighbouring glyphs (white on a dark bar).
        return (
            <span
                className={ `nav-tab-logo nav-tab-logo--tint${extra}` }
                style={{
                    WebkitMaskImage: `url("${uri}")`,
                    maskImage: `url("${uri}")`,
                }}
                aria-hidden="true"
            />
        );
    }

    if (resolved.kind === 'icomoon') {
        return (
            <span
                className={ `icon-icomoon${extra}` }
                style={ style }
                aria-hidden="true"
            >
                { String.fromCodePoint(resolved.code) }
            </span>
        );
    }

    return <i className={ `${resolved.className}${extra}` } style={ style } aria-hidden="true" />;
}
